// Highlight layer: overlay boxes drawn above the page, never touching host DOM.
// aria-hidden so QC Lens picker chrome never pollutes the host a11y tree.
// The floating tooltip is offset OUTSIDE the element bounds so it can't occlude
// the target; dimensions live once in the dialog, not here (dedup).

export class HighlightLayer {
  private layer: HTMLElement
  private box: HTMLElement
  private tag: HTMLElement
  private spot: HTMLElement
  private tracked: Element | null = null
  private locked = false
  private spotlight = false
  private raf = 0

  constructor(root: ShadowRoot) {
    this.layer = document.createElement('div')
    this.layer.className = 'qc-layer'
    this.layer.setAttribute('aria-hidden', 'true')
    this.box = document.createElement('div')
    this.box.className = 'qc-hl'
    this.tag = document.createElement('div')
    this.tag.className = 'qc-tag'
    this.spot = document.createElement('div')
    this.spot.className = 'qc-spot'
    this.spot.style.display = 'none'
    this.layer.append(this.spot, this.box, this.tag)
    this.hide()
    root.appendChild(this.layer)
    const onMove = () => this.schedule()
    window.addEventListener('scroll', onMove, { capture: true, passive: true })
    window.addEventListener('resize', onMove, { passive: true })
  }

  show(el: Element, label: string, opts: { locked?: boolean; lost?: boolean; spotlight?: boolean } = {}): void {
    this.tracked = el
    this.locked = !!opts.locked
    this.spotlight = !!opts.spotlight
    this.box.classList.toggle('qc-locked', this.locked)
    this.box.classList.toggle('qc-lost', !!opts.lost)
    this.box.classList.remove('qc-flash')
    this.tag.innerHTML = label
    this.box.style.display = 'block'
    this.tag.style.display = 'block'
    this.spot.style.display = this.spotlight ? 'block' : 'none'
    this.position()
  }

  /** Brief 2s flash on an element (session-row re-highlight). */
  flash(el: Element, label: string): void {
    this.show(el, label)
    this.box.classList.add('qc-flash')
    setTimeout(() => {
      if (!this.locked) this.hide()
    }, 2100)
  }

  hide(): void {
    this.tracked = null
    this.locked = false
    this.spotlight = false
    this.box.style.display = 'none'
    this.tag.style.display = 'none'
    this.spot.style.display = 'none'
  }

  markLost(): void {
    this.box.classList.add('qc-lost')
    this.spot.style.display = 'none'
  }

  private schedule(): void {
    if (this.raf || !this.tracked) return
    this.raf = requestAnimationFrame(() => {
      this.raf = 0
      this.position()
    })
  }

  private position(): void {
    const el = this.tracked
    if (!el) return
    if (!el.isConnected) {
      this.markLost()
      return
    }
    const r = el.getBoundingClientRect()
    Object.assign(this.box.style, {
      left: `${r.left}px`,
      top: `${r.top}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
    })
    if (this.spotlight) {
      Object.assign(this.spot.style, {
        left: `${r.left - 4}px`,
        top: `${r.top - 4}px`,
        width: `${r.width + 8}px`,
        height: `${r.height + 8}px`,
      })
    }
    // Tooltip offset outside the element: above, flipping below when clipped.
    const tagH = this.tag.offsetHeight || 24
    const top = r.top - tagH - 8 >= 0 ? r.top - tagH - 8 : r.bottom + 8
    const left = Math.max(8, Math.min(r.left, window.innerWidth - this.tag.offsetWidth - 8))
    Object.assign(this.tag.style, { left: `${left}px`, top: `${top}px` })
  }
}

/** Tooltip label — selector only (dimensions live once, in the dialog). */
export function elementLabel(el: Element): string {
  let name = el.tagName.toLowerCase()
  if (el.id) name += `#${el.id}`
  else if (el.classList.length) name += `.${el.classList[0]}`
  return escapeHtml(name)
}

/** Spoken description for the aria-live region, e.g. "button, New group, 116 by 38 pixels". */
export function elementSpeech(el: Element): string {
  const r = el.getBoundingClientRect()
  const tag = el.tagName.toLowerCase()
  const text = (el as HTMLElement).innerText?.trim().slice(0, 40) || el.id || el.className || ''
  const dims = `${Math.round(r.width)} by ${Math.round(r.height)} pixels`
  return [tag, text, dims].filter(Boolean).join(', ')
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
}
